/**
 * A cohort at Logiscool: the participant states and the question asked
 * before enrolling everyone ready. Pure, so tested.
 */

export const LIMO_STATES = {
  ready: { label: "gata", cls: "bg-[#EAF4EE] text-[#2F6B4E]" },
  blocked: { label: "blocat", cls: "bg-[#FDECEC] text-[#9b1f2c]" },
  waiting: { label: "așteaptă contractul", cls: "bg-[#FDF3E7] text-[#8a6a1a]" },
  enrolled: { label: "înscris", cls: "bg-[#EDEEF0] text-[#373A45]" },
};

export function enrolQuestion(data, sandbox) {
  const n = data?.counts?.ready || 0;
  const free = data?.capacity?.free;
  const who = n === 1 ? "1 participant" : `${n} participanți`;
  const cost = sandbox ? "din pool-urile de probă (nicio licență reală)" : `${n} ${n === 1 ? "licență" : "licențe"} de cursant`;
  const left = typeof free === "number" ? ` Licențe libere acum: ${free}.` : "";
  const short = typeof free === "number" && free < n
    ? ` Ajung doar pentru ${free}; ceilalți rămân pentru când se eliberează sau se adaugă licențe.` : "";
  return `Înscrii în Logiscool ${who} cu contractul semnat? Se consumă ${cost}.${left}${short} `
    + "Fiecare primește imediat pe email utilizatorul și parola.";
}

/** What the toast says after a trainer's access email left. */
export function trainerAccessNote(result, sandbox) {
  if (sandbox) return "Datele de acces ți-au fost trimise ție, ca probă.";
  return result?.username_only
    ? "Trainerului i-a plecat numele de utilizator; își păstrează parola contului existent."
    : "Datele de acces au fost trimise trainerului pe email.";
}

/**
 * Why Logiscool gave us no login for a trainer, in words — with their code
 * attached, because a generic "try again" hid the cause on its first real use.
 */
export function trainerCredsFailure(r) {
  const code = r?.error || r?.detail?.error;
  const d = { ...(r?.detail || {}), ...(r || {}) };
  if (code === "trainer_not_found") {
    return `Logiscool nu îl are pe trainer în lista unității (am primit ${d.listed ?? 0} traineri), nici după id, email sau utilizator. `
      + "Verifică în MyLogiscool dacă există și cu ce email.";
  }
  if (code === "not_configured") return "Cheile Logiscool nu sunt configurate pe server.";
  if (code === "unexpected_shape") return "Logiscool a răspuns într-o formă neașteptată la lista de traineri (unexpected_shape).";
  if (code) return `Logiscool nu a returnat datele trainerului (${code}${d.status ? `, HTTP ${d.status}` : ""}).`;
  return "Logiscool nu a returnat datele trainerului.";
}

/**
 * Logiscool drops a username already taken anywhere on their side and makes
 * the account with no login, silently (22.09.2026). The only fix is a new one.
 */
export const USERNAME_TAKEN_FIX = "Contul Logiscool s-a creat fără nume de utilizator: numele ales era deja folosit la Logiscool. "
  + "Șterge trainerul și creează-l din nou cu alt „Utilizator MyLogiscool” (de exemplu cu inițiala tatălui).";

/** Why the access email did not leave. */
export function trainerSendFailure(r) {
  switch (r?.reason) {
    case "credentials_unavailable": return trainerCredsFailure(r);
    case "email_failed": return "Datele există, dar emailul nu a plecat (Resend). Încearcă din nou peste un minut.";
    case "no_username": return USERNAME_TAKEN_FIX;
    case "redacted": return "Parola nu a putut fi citită de la Logiscool; emailul nu a plecat. Folosește „Resetează parola”.";
    case "not_in_limo": return "Trainerul nu e încă creat în Logiscool.";
    default: return "Emailul nu a plecat. Încearcă din nou peste un minut sau resetează parola.";
  }
}

const STEP_LABELS = {
  start: "Început",
  enrolled_in_a: "Înscris în grupa A",
  removed_from_a: "Scos din grupa A",
  added_to_b: "Pus în grupa B",
  also_in_a: "Pus și înapoi în A (în două grupe)",
  removed_from_both: "Scos din ambele",
  disabled: "Cont dezactivat",
};
export const experimentStepLabel = (step) => STEP_LABELS[step] || step;

/** The licence experiment's answers, in words. A delta is the change in occupied student licences. */
export function experimentAnswers(a) {
  if (!a) return [];
  const say = (d, yes, no, zero = no) => (d === null || d === undefined ? "nu s-a putut măsura" : d > 0 ? yes : d < 0 ? no : zero);
  return [
    ["Înscrierea consumă o licență", say(a.enrol_spends, `da (+${a.enrol_spends})`, "nu", "nu s-a schimbat nimic")],
    ["Scoaterea din grupă eliberează licența", say(a.removal_frees, "nu, a crescut", `da (${a.removal_frees})`, "nu, licența rămâne ocupată")],
    ["Mutarea în altă grupă consumă o licență nouă", say(a.re_add_spends, `da (+${a.re_add_spends})`, "nu", "nu, o refolosește")],
    ["În două grupe deodată se consumă două licențe", say(a.second_group_spends, `da (+${a.second_group_spends})`, "nu", "nu, una ajunge")],
    ["Dezactivarea contului schimbă licențele ocupate", say(a.disable, `da (+${a.disable})`, `da (${a.disable})`, "nu (doar le suspendă)")],
  ];
}

LIMO_STATES.to_move = { label: "de mutat", cls: "bg-[#EDE3F2] text-[#6B3F7A]" };

/** A participant who left: what happened at Logiscool, in one line. */
export function releaseNote(release, releasedAt, fmt = (d) => new Date(d).toLocaleDateString("ro-RO")) {
  if (!release) return null;
  // Reactivated by the team (08.10.2026) without being put back in a group.
  if (release.reactivated_at) {
    return { tone: "done", text: `Cont reactivat pe ${fmt(release.reactivated_at)}, dar nu e în nicio grupă Logiscool `
      + "(scos la retragere), deci nu are licență." };
  }
  if (releasedAt) return { tone: "done", text: `Scos din grupa Logiscool pe ${fmt(releasedAt)}, cont dezactivat.` };
  const done = [release.removed_at && "scos din grupă", release.disabled_at && "cont dezactivat"].filter(Boolean);
  return {
    tone: "pending",
    text: `Retragere la Logiscool în curs${done.length ? ` (${done.join(", ")})` : ""}: se reîncearcă automat la 15 minute.`
      + (release.error ? ` Ultima eroare: ${release.error}.` : ""),
  };
}

/** The question asked before moving a participant at Logiscool too. */
export function moveQuestion(move) {
  const from = move?.current_group_name ? `„${move.current_group_name}”` : "grupa veche";
  const to = move?.target_group_name ? `„${move.target_group_name}”` : "grupa nouă";
  return `Îl mut și în Logiscool, din ${from} în ${to}? Întâi e adăugat în grupa nouă, apoi scos din cea veche; `
    + "nu se consumă nicio licență în plus și nu rămâne fără acces.";
}

/** What each difference found by the comparison with Logiscool means (PR L3). */
export const RECON_KINDS = {
  missing_group: { label: "Grupa nu există la Logiscool", tone: "bad",
    hint: () => "Id-ul salvat la noi nu mai e recunoscut. Din Grupe curs → Logiscool: „Dezleagă”, apoi creeaz-o din nou." },
  shared_group: { label: "Aceeași grupă Logiscool ca altă grupă", tone: "bad",
    hint: (i) => `O folosește și: ${i.detail}. Din Grupe curs → Logiscool: „Dezleagă”, apoi creeaz-o din nou.` },
  name_differs: { label: "Alt nume la Logiscool", tone: "warn", action: "rename",
    hint: (i) => `La Logiscool: „${i.detail}”.` },
  trainer_differs: { label: "Alt trainer la Logiscool", tone: "warn", action: "trainer",
    hint: (i) => `Aici e ${i.detail || "alt trainer"}.` },
  missing_there: { label: "Înscris aici, lipsește la Logiscool", tone: "bad",
    hint: () => "A fost scos direct din MyLogiscool sau înscrierea n-a ajuns. Verifică în fișa lui." },
  unknown_there: { label: "La Logiscool, dar nu în grupa noastră", tone: "warn",
    hint: (i) => (i.enrollment_id
      ? `Înscrierea lui e acum „${i.detail}”; dacă s-a retras, retragerea n-a ajuns încă la Logiscool.`
      : "Nu corespunde niciunei înscrieri de la noi: adăugat direct în MyLogiscool.") },
  email_differs: { label: "Alt email la Logiscool", tone: "info",
    hint: (i) => `La Logiscool a rămas ${i.detail}. API-ul nu permite schimbarea; utilizatorul și parola rămân valabile.` },
  members_unreadable: { label: "Membrii nu au putut fi citiți", tone: "warn", hint: () => "Încearcă din nou mai târziu." },
};
